import 'context-filter-polyfill'; // Safari hack

import { getFaces } from '../faceDetection';

async function blurFaces<Image extends { uri: string }>(
	images: Image[],
	faceDescriptorToExclude?: Float32Array[],
) {
	const htmlImages = images.map((image) => {
		const dummyImage = new Image();
		dummyImage.src = image.uri;

		return dummyImage;
	});
	const facesPositionsOnImages = await getFaces(htmlImages, faceDescriptorToExclude);

	return await Promise.all(
		facesPositionsOnImages.map((imageWithFaces, imageIndex) => {
			return blurFacesOn(
				images[imageIndex].uri,
				imageWithFaces.map((face) => ({
					x: face.left,
					y: face.top,
					width: face.width,
					height: face.height,
				})),
			);
		}),
	);
}

function blurFacesOn(
	imageSrc: string,
	blurBoxes: { x: number; y: number; width: number; height: number }[],
) {
	const [virtualCanvas, ctx] = get2DCanvas({ canvasContextOptions: { willReadFrequently: true } });

	const img = new Image();
	img.src = imageSrc;

	return new Promise<string>((resolve, reject) => {
		img.onload = async () => {
			virtualCanvas.width = img.width;
			virtualCanvas.height = img.height;

			ctx.drawImage(img, 0, 0);

			blurBoxes.forEach((blurBox) => {
				drawBlurredBox(ctx, blurBox);
			});

			const blurredImageData = await virtualCanvas.convertToBlob();

			const blurredImageDataURL = await getDataURLFrom(blurredImageData);
			const imageBase64 = getBase64(blurredImageDataURL);

			resolve(imageBase64);
		};

		img.onerror = () => {
			reject(new Error('Failed to load the image for blurring'));
		};
	});
}

function getDataURLFrom(blob: Blob) {
	const reader = new FileReader();
	reader.readAsDataURL(blob);

	return new Promise<string>((resolve, reject) => {
		reader.addEventListener(
			'load',
			() => {
				resolve(reader.result?.toString() || '');
			},
			false,
		);
		reader.addEventListener('error', reject, false);
	});
}

function drawBlurredBox(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	blurBox: { x: number; y: number; width: number; height: number },
) {
	const BLUR_RADIUS = 8;
	const imageData = ctx.getImageData(blurBox.x, blurBox.y, blurBox.width, blurBox.height);

	const [offCanvas, offCtx] = get2DCanvas({ width: blurBox.width, height: blurBox.height });

	offCtx.putImageData(imageData, 0, 0);
	offCtx.filter = `blur(${BLUR_RADIUS}px)`;
	offCtx.drawImage(offCanvas, 0, 0);

	const blurredData = offCtx.getImageData(0, 0, blurBox.width, blurBox.height);
	ctx.putImageData(blurredData, blurBox.x, blurBox.y);
}

function get2DCanvas({
	width = 0,
	height = 0,
	canvasContextOptions,
}: {
	width?: number;
	height?: number;
	canvasContextOptions?: CanvasRenderingContext2DSettings;
}) {
	const canvasElement = new OffscreenCanvas(width, height);
	if (width) {
		canvasElement.width = width;
	}
	if (height) {
		canvasElement.height = height;
	}

	const ctx = canvasElement.getContext('2d', canvasContextOptions);

	if (!ctx) {
		throw new Error('Could not get canvas 2D context');
	}

	return [canvasElement, ctx] as const;
}

function getBase64(dataURL: string) {
	return dataURL.replace('data:', '').replace(/^.+,/, '');
}

export { blurFaces };
